import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  AlignLeft,
  ArrowLeft,
  BookmarkCheck,
  CalendarDays,
  Clock,
  ExternalLink,
  Info,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Tag,
  Users,
  type LucideIcon,
} from "lucide-react";
import { GetEventsAPI, type StudentEvent } from "@/services/api/user/events";
import { CheckSubscriptEventAPI } from "@/services/api/user/subscribeEvent";
import {
  formatDateRange,
  formatDeadline,
  formatTimeRange,
  localized,
  postTypeLabelKey,
  toContactList,
  toHashTags,
  toImageList,
  toUniversityCodes,
  withMomentLocale,
  type ContactEntry,
  POST_TYPE_BADGE_CLASS,
} from "@/utils/eventFormat";
import StudentEventCarousel from "@/components/student/StudentEventCarousel";
import StudentOrganizerList from "@/components/student/StudentOrganizerList";
import StudentSubscribeButton from "@/components/student/StudentSubscribeButton";
import StudentUniversityLogos from "@/components/student/StudentUniversityLogos";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminListSkeleton from "@/components/AdminListSkeleton";

const LOOKUP_PAGES = 5;

/** Post types people can actually join; the others are informational only. */
const JOINABLE_TYPES = ["EVENT", "SOCIETY_ACTIVITY", "RECRUITMENT"];

type DetailRow = { id: string; icon: LucideIcon; label: string; value: string };

/** Card wrapper of one details section, with its uppercase section heading. */
const SectionCard: React.FC<{
  icon: LucideIcon;
  title?: string;
  children: React.ReactNode;
}> = ({ icon: Icon, title, children }) => (
  <section className="mt-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
    {title && (
      <h2 className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-400">
        <Icon size={14} className="shrink-0 text-[#ffa217]" />
        {title}
      </h2>
    )}
    {children}
  </section>
);

/**
 * Events scraped from Instagram store their origin post as a
 * `{ input1: "Instagram", input2: <permalink> }` entry in `links`, which then
 * renders as a duplicate "Instagram" row. The organizer / source account is
 * already shown above, so the attribution row is dropped; registration,
 * email and other real links are kept.
 */
const isInstagramSourceLink = (
  entry: ContactEntry,
  sourceUrl?: string | null,
): boolean => {
  if (sourceUrl && entry.value === sourceUrl) return true;
  return (
    entry.label.trim().toLowerCase() === "instagram" &&
    /instagram\.com/i.test(entry.value) &&
    /\/p\//.test(entry.value)
  );
};

/**
 * Event details screen — the web port of the RN `EventDetailsScreen`. The event
 * record is handed over through the router state by the feed, list and
 * calendar; when the URL is opened directly it is resolved by scanning the
 * feed. Renders the full record plus the subscribe and follow-organizer
 * actions, with a bottom sheet for the target-audience breakdown.
 */
const StudentEventDetailsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const language = useSelector(
    (state: any) => state.setting?.language ?? "zh_hant",
  );
  withMomentLocale(i18n.language);
  const { eventId } = useParams<{ eventId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const stateEvent = (location.state as { event?: StudentEvent } | null)?.event;

  const [event, setEvent] = useState<StudentEvent | null>(stateEvent ?? null);
  const [loading, setLoading] = useState(!stateEvent);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!stateEvent) return;
    setEvent(stateEvent);
    setNotFound(false);
    setLoading(false);
  }, [stateEvent]);

  useEffect(() => {
    if (stateEvent || !eventId) return;
    let cancelled = false;
    const lookup = async () => {
      setLoading(true);
      for (let page = 1; page <= LOOKUP_PAGES; page += 1) {
        const resp = await GetEventsAPI({ page });
        const result = resp?.result;
        if (!Array.isArray(result)) break;
        const match = result.find((item) => item.details.id === eventId);
        if (match) {
          if (cancelled) return;
          setEvent(match);
          setLoading(false);
          const checkResp = await CheckSubscriptEventAPI(eventId);
          if (cancelled) return;
          setEvent((prev) =>
            prev ? { ...prev, subscribed: checkResp?.status === 0 } : prev,
          );
          return;
        }
        if (result.length < 10) break;
      }
      if (cancelled) return;
      setLoading(false);
      setNotFound(true);
    };
    lookup();
    return () => {
      cancelled = true;
    };
  }, [stateEvent, eventId]);

  useEffect(() => {
    if (!stateEvent || !eventId) return;
    let cancelled = false;
    const refreshSubscribed = async () => {
      const checkResp = await CheckSubscriptEventAPI(eventId);
      if (cancelled) return;
      setEvent((prev) =>
        prev ? { ...prev, subscribed: checkResp?.status === 0 } : prev,
      );
    };
    refreshSubscribed();
    return () => {
      cancelled = true;
    };
  }, [stateEvent, eventId]);

  const handleSubscribedChange = useCallback((subscribed: boolean) => {
    setEvent((prev) => (prev ? { ...prev, subscribed } : prev));
  }, []);

  const handleFollowChange = useCallback(
    (organizerId: string, following: boolean) => {
      setEvent((prev) =>
        prev
          ? {
              ...prev,
              organizers: (prev.organizers ?? []).map((organizer) =>
                organizer.id === organizerId
                  ? { ...organizer, following }
                  : organizer,
              ),
            }
          : prev,
      );
    },
    [],
  );

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-6">
        <AdminListSkeleton count={3} />
      </div>
    );
  }

  if (!event || notFound) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconType="Ionicons"
            iconName="alert-circle-outline"
            title={t("Event not found")}
            message={t("This event is no longer available.")}
            actionLabel={t("Back to Home")}
            onAction={() => navigate("/student/home")}
          />
        </div>
      </div>
    );
  }

  const { details } = event;
  const organizers = event.organizers ?? [];
  const coverImages = toImageList(details.event_images);
  const eventTitle = localized(details.title, details.title_en, language);
  const eventDescription = localized(
    details.description,
    details.description_en,
    language,
  );
  const eventLocation = localized(details.location, details.location_en, language);
  const whoCanJoin = localized(
    details.who_can_join,
    details.who_can_join_en,
    language,
  ).trim();
  const isJoinable = JOINABLE_TYPES.includes(details?.post_type ?? "");
  const postTypeKey = postTypeLabelKey(details?.post_type);
  const hashTags = toHashTags(details.hash_tags);
  const emails = toContactList(details.emails, t("Email"));
  const phones = toContactList(details.phones, t("Phone"));
  const links = toContactList(details.links, t("Link")).filter(
    (entry) => !isInstagramSourceLink(entry, details.source_url),
  );
  const dateLabel = formatDateRange(details.start_date, details.end_date);
  const timeLabel = formatTimeRange(details.start_time, details.end_time);
  const deadlineLabel = formatDeadline(
    details.deadline_date,
    details.deadline_time,
  );

  const detailRows: DetailRow[] = [
    dateLabel
      ? { id: "date", icon: CalendarDays, label: t("Date"), value: dateLabel }
      : null,
    timeLabel
      ? { id: "time", icon: Clock, label: t("Time"), value: timeLabel }
      : null,
    eventLocation
      ? {
          id: "location",
          icon: MapPin,
          label: t("Location"),
          value: eventLocation,
        }
      : null,
  ].filter(Boolean) as DetailRow[];

  const contactCount = emails.length + phones.length + links.length;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 pb-16">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 transition hover:text-[#ffa217]"
      >
        <ArrowLeft size={16} />
        {t("Back")}
      </button>

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="p-5 pb-4">
          {(postTypeKey || event.subscribed) && (
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              {postTypeKey && (
                <span className={POST_TYPE_BADGE_CLASS}>
                  {t(postTypeKey)}
                </span>
              )}
              {event.subscribed && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#fff3e0] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#ffa217]">
                  <BookmarkCheck size={10} />
                  {t("Subscribed")}
                </span>
              )}
            </div>
          )}
          <h1 className="text-2xl font-extrabold leading-8 text-gray-900">
            {eventTitle || t("Untitled Event")}
          </h1>
        </div>

        {coverImages.length > 0 && (
          <StudentEventCarousel
            images={coverImages}
            alt={eventTitle || t("Untitled Event")}
          />
        )}

        <div className="p-5">
          <div className="flex items-center justify-between gap-3">
            <StudentUniversityLogos codes={toUniversityCodes(details.university)} />
            <div className="ml-auto">
              <StudentSubscribeButton
                eventId={details.id}
                subscribed={Boolean(event.subscribed)}
                onChange={handleSubscribedChange}
                variant="icon"
              />
            </div>
          </div>
        </div>
      </div>

      {(detailRows.length > 0 || deadlineLabel) && (
        <SectionCard icon={CalendarDays}>
          {deadlineLabel && (
            <div className="mb-3 inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-600">
              {t("Application Deadline")} · {deadlineLabel}
            </div>
          )}
          {detailRows.length > 0 && (
            <div className="space-y-2">
              {detailRows.map((row) => (
                <div
                  key={row.id}
                  className="flex items-center gap-3 text-sm"
                >
                  <row.icon size={16} className="shrink-0 text-[#ffa217]" />
                  <span className="w-24 shrink-0 text-xs font-bold uppercase tracking-wider text-gray-400">
                    {row.label}
                  </span>
                  <span className="min-w-0 flex-1 font-medium text-gray-700">
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      <SectionCard icon={AlignLeft} title={t("Description")}>
        <p className="whitespace-pre-line text-[15px] leading-relaxed text-gray-700">
          {eventDescription || t("No description provided.")}
        </p>
      </SectionCard>

      {organizers.length > 0 && (
        <SectionCard icon={Users} title={t("Organizers")}>
          <StudentOrganizerList
            organizers={organizers}
            onToggleFollow={handleFollowChange}
          />
        </SectionCard>
      )}

      {hashTags.length > 0 && (
        <SectionCard icon={Tag} title={t("Tags")}>
          <div className="flex flex-wrap items-center gap-1.5">
            {hashTags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-[#fff3e0] px-3 py-1 text-xs font-semibold text-[#ffa217]"
              >
                #{tag}
              </span>
            ))}
          </div>
        </SectionCard>
      )}

      {contactCount > 0 && (
        <SectionCard icon={Mail} title={t("Contact")}>
          <div className="space-y-2">
            {emails.map((entry, index) => (
              <div
                key={`email-${index}`}
                className="flex items-center gap-3 text-sm"
              >
                <Mail size={16} className="shrink-0 text-[#ffa217]" />
                <span className="min-w-0 flex-1 truncate text-gray-500">
                  {entry.label}
                </span>
                <a
                  href={`mailto:${entry.value}`}
                  className="max-w-[50%] shrink-0 truncate font-semibold text-[#ffa217] hover:underline"
                >
                  {entry.value}
                </a>
              </div>
            ))}
            {phones.map((entry, index) => (
              <div
                key={`phone-${index}`}
                className="flex items-center gap-3 text-sm"
              >
                <Phone size={16} className="shrink-0 text-[#ffa217]" />
                <span className="min-w-0 flex-1 truncate text-gray-500">
                  {entry.label}
                </span>
                <a
                  href={`tel:+852${entry.value}`}
                  className="max-w-[50%] shrink-0 truncate font-semibold text-[#ffa217] hover:underline"
                >
                  {entry.value}
                </a>
                <a
                  href={`https://wa.me/852${entry.value}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex shrink-0 items-center gap-1 rounded-lg border border-gray-100 px-2.5 py-1.5 text-xs font-semibold text-[#ffa217] transition-colors hover:border-[#ffa217] hover:bg-[#fff3e0]"
                >
                  <MessageCircle size={14} />
                  {t("WhatsApp")}
                </a>
              </div>
            ))}
            {links.map((entry, index) => (
              <div
                key={`link-${index}`}
                className="flex items-center gap-3 text-sm"
              >
                <ExternalLink size={16} className="shrink-0 text-[#ffa217]" />
                <span className="min-w-0 flex-1 truncate text-gray-500">
                  {entry.label}
                </span>
                <a
                  href={entry.value}
                  target="_blank"
                  rel="noreferrer"
                  className="flex shrink-0 items-center gap-1 rounded-lg border border-gray-100 px-2.5 py-1.5 text-xs font-semibold text-[#ffa217] transition-colors hover:border-[#ffa217] hover:bg-[#fff3e0]"
                >
                  <ExternalLink size={16} />
                  {t("Open link")}
                </a>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {isJoinable && (
        <SectionCard icon={Info} title={t("Who can join?")}>
          <p className="whitespace-pre-line text-sm leading-6 text-gray-700">
            {whoCanJoin || t("Everyone can join")}
          </p>
        </SectionCard>
      )}
    </div>
  );
};

export default StudentEventDetailsPage;
